import React, { useState, useRef } from 'react';
import axios from './lib/axiosClient';

const IMPORT_TYPES = [
  { key: 'contacts', label: 'Customers', icon: '👥', desc: 'Import your customer list', fields: ['first_name', 'last_name', 'phone', 'email', 'notes', 'birthday'], required: ['first_name'] },
  { key: 'services', label: 'Services', icon: '✂️', desc: 'Import your service menu', fields: ['name', 'duration', 'price', 'description', 'category', 'member_price'], required: ['name', 'duration', 'price'] },
  { key: 'products', label: 'Products', icon: '📦', desc: 'Import inventory items', fields: ['name', 'sku', 'retail_price', 'cost', 'quantity_on_hand', 'category', 'reorder_point', 'description'], required: ['name', 'retail_price'] },
  { key: 'staff', label: 'Staff', icon: '👔', desc: 'Import team members', fields: ['first_name', 'last_name', 'email', 'phone', 'role'], required: ['first_name'] },
];

function Import() {
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };
    const parseRow = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') { inQuotes = !inQuotes; }
        else if (line[i] === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += line[i]; }
      }
      result.push(current.trim());
      return result;
    };
    const hdrs = parseRow(lines[0]);
    const data = lines.slice(1).map(l => {
      const vals = parseRow(l);
      const obj = {};
      hdrs.forEach((h, i) => { obj[h] = vals[i] || ''; });
      return obj;
    }).filter(r => Object.values(r).some(v => v));
    return { headers: hdrs, rows: data };
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers: h, rows: r } = parseCSV(e.target.result);
      setHeaders(h);
      setRows(r);
      // Auto-map matching column names
      const autoMap = {};
      const typeInfo = IMPORT_TYPES.find(t => t.key === importType);
      if (typeInfo) {
        typeInfo.fields.forEach(field => {
          const match = h.find(col =>
            col.toLowerCase().replace(/[\s_-]+/g, '') === field.toLowerCase().replace(/[\s_-]+/g, '') ||
            col.toLowerCase().includes(field.toLowerCase().replace(/_/g, ' '))
          );
          if (match) autoMap[field] = match;
        });
      }
      setMapping(autoMap);
      setStep(3);
    };
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.txt') || f.type === 'text/csv' || f.type === 'text/plain' || f.type === 'application/vnd.ms-excel')) handleFile(f);
  };

  const handleImport = async () => {
    const typeInfo = IMPORT_TYPES.find(t => t.key === importType);
    const missing = typeInfo.required.filter(f => !mapping[f]);
    if (missing.length > 0) {
      alert('Please map required fields: ' + missing.join(', '));
      return;
    }
    setImporting(true);
    try {
      const records = rows.map(row => {
        const mapped = {};
        Object.entries(mapping).forEach(([field, col]) => {
          if (col) mapped[field] = row[col];
        });
        return mapped;
      });
      const res = await axios.post(`/api/import/${importType}`, { records });
      if (res.data.success) {
        setResults(res.data.results);
        setStep(4);
      } else {
        alert('Import failed: ' + (res.data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
    setImporting(false);
  };

  const reset = () => {
    setStep(1); setImportType(null); setFile(null); setHeaders([]); setRows([]); setMapping({}); setResults(null);
  };

  const s = {
    page: { padding: '32px', maxWidth: '900px', margin: '0 auto' },
    title: { color: '#fff', fontSize: '26px', fontWeight: '800', margin: '0 0 4px', letterSpacing: '-0.5px' },
    subtitle: { color: '#888', fontSize: '14px', margin: '0 0 28px' },
    card: { background: '#12121f', borderRadius: '16px', padding: '24px', border: '1px solid #1e1e35', marginBottom: '16px', cursor: 'pointer', transition: 'all 0.2s' },
    cardActive: { borderColor: '#ff6b2b', background: '#1a1428' },
    stepBadge: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '20px' },
    btn: { padding: '14px 28px', borderRadius: '12px', border: 'none', fontSize: '15px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s' },
    btnPrimary: { background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff' },
    btnSecondary: { background: '#1e1e35', color: '#ccc', border: '1px solid #2a2a3a' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
    th: { padding: '10px 12px', background: '#252840', color: '#888', fontWeight: '600', textAlign: 'left', borderBottom: '1px solid #2a2a3a' },
    td: { padding: '10px 12px', color: '#ccc', borderBottom: '1px solid #1e1e35' },
    select: { width: '100%', padding: '8px 12px', background: '#1a1c2e', border: '1px solid #2a2a3a', borderRadius: '8px', color: '#fff', fontSize: '13px' },
  };

  const downloadTemplate = (type) => {
    var t = IMPORT_TYPES.find(i => i.key === type);
    if (!t) return;
    var csv = t.fields.join(',') + '\n';
    var examples = {
      contacts: 'Jane,Smith,7025551234,jane@email.com,Regular client,1990-03-15',
      services: 'Deep Tissue Massage,60,120,Full body deep tissue,Massage,100',
      products: 'Massage Oil,OIL-001,24.99,12.50,50,Supplies,10,Organic lavender massage oil',
      staff: 'Sarah,Johnson,sarah@shop.com,7025559876,therapist',
    };
    if (examples[type]) csv += examples[type];
    var blob = new Blob([csv], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = type + '-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div>
          <h1 style={s.title}>📥 Import Data</h1>
          <p style={s.subtitle}>Upload CSV files to import customers, services, products, or staff</p>
        </div>
        {step > 1 && (
          <button onClick={reset} style={{ ...s.btn, ...s.btnSecondary, padding: '10px 20px', fontSize: '13px' }}>← Start Over</button>
        )}
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
        {['Select Type', 'Upload CSV', 'Map Fields', 'Results'].map((label, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: '700',
              background: step > i + 1 ? '#ff6b2b' : step === i + 1 ? '#ff6b2b' : '#1e1e35',
              color: step >= i + 1 ? '#fff' : '#555',
            }}>{step > i + 1 ? '✓' : i + 1}</div>
            <span style={{ fontSize: '12px', fontWeight: '600', color: step === i + 1 ? '#fff' : '#555' }}>{label}</span>
            {i < 3 && <div style={{ flex: 1, height: '2px', background: step > i + 1 ? '#ff6b2b' : '#1e1e35', borderRadius: '1px' }} />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Type */}
      {step === 1 && (
        <div>
          {/* Quick tip for new businesses */}
          <div style={{ background: '#ff6b2b08', borderRadius: 14, padding: '16px 20px', border: '1px solid #ff6b2b15', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>💡</span>
            <div>
              <span style={{ color: '#ff8f40', fontSize: 13, fontWeight: 700 }}>Fastest way to get started</span>
              <span style={{ color: '#888', fontSize: 13 }}> — Export your customer list from your old system, save as CSV, and upload it here. We'll map the columns automatically.</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
            {IMPORT_TYPES.map(t => (
              <div key={t.key}
                style={{ ...s.card, display: 'flex', flexDirection: 'column' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b2b'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e35'; e.currentTarget.style.transform = 'none'; }}
              >
                <div onClick={() => { setImportType(t.key); setStep(2); }} style={{ flex: 1, cursor: 'pointer' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>{t.icon}</div>
                  <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 6px' }}>{t.label}</h3>
                  <p style={{ color: '#888', fontSize: '13px', margin: '0 0 12px' }}>{t.desc}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: 12 }}>
                    {t.fields.slice(0, 4).map(f => (
                      <span key={f} style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '11px', background: '#252840', color: '#aaa' }}>
                        {f.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {t.fields.length > 4 && <span style={{ padding: '3px 10px', fontSize: '11px', color: '#666' }}>+{t.fields.length - 4} more</span>}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadTemplate(t.key); }}
                  style={{ background: '#252840', border: '1px solid #2a2a3a', borderRadius: 8, padding: '6px 12px', color: '#888', fontSize: 11, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' }}
                >
                  ⬇ Download Template
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Upload CSV */}
      {step === 2 && (
        <div>
          <div style={{ ...s.stepBadge, background: '#ff6b2b15', color: '#ff6b2b' }}>
            {IMPORT_TYPES.find(t => t.key === importType)?.icon} Importing {IMPORT_TYPES.find(t => t.key === importType)?.label}
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? '#ff6b2b' : '#2a2a3a'}`,
              borderRadius: '20px', padding: '60px 40px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? '#ff6b2b08' : '#1a1c2e', transition: 'all 0.2s',
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
              {dragOver ? 'Drop your CSV here!' : 'Drag & drop your CSV file'}
            </h3>
            <p style={{ color: '#888', fontSize: '14px', margin: '0 0 20px' }}>or click to browse files</p>
            <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain,application/csv,application/vnd.ms-excel" style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
              style={{ padding: '12px 28px', borderRadius: '10px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', fontSize: '14px', fontWeight: '700', border: 'none', cursor: 'pointer' }}>
              Choose File
            </button>
          </div>

          {/* CSV Format Help */}
          <div style={{ marginTop: '24px', background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #1e1e35' }}>
            <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', margin: '0 0 12px' }}>📋 Expected columns for {IMPORT_TYPES.find(t => t.key === importType)?.label}:</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {IMPORT_TYPES.find(t => t.key === importType)?.fields.map(f => {
                const isRequired = IMPORT_TYPES.find(t => t.key === importType)?.required.includes(f);
                return (
                  <span key={f} style={{
                    padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                    background: isRequired ? '#ff6b2b15' : '#252840',
                    color: isRequired ? '#ff6b2b' : '#888',
                    border: isRequired ? '1px solid #ff6b2b30' : '1px solid transparent',
                  }}>
                    {f.replace(/_/g, ' ')}{isRequired ? ' *' : ''}
                  </span>
                );
              })}
            </div>
            <p style={{ color: '#555', fontSize: '12px', margin: '10px 0 0' }}>* Required fields. Column names are matched automatically.</p>
          </div>

          <button onClick={() => setStep(1)} style={{ ...s.btn, ...s.btnSecondary, marginTop: '16px', padding: '10px 20px', fontSize: '13px' }}>← Back</button>
        </div>
      )}

      {/* Step 3: Map Fields & Preview */}
      {step === 3 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <div style={{ ...s.stepBadge, background: '#ff6b2b15', color: '#ff6b2b', display: 'inline-flex' }}>
                📄 {file?.name}
              </div>
              <p style={{ color: '#888', fontSize: '13px', margin: '4px 0 0' }}>{rows.length} records found · {headers.length} columns detected</p>
            </div>
          </div>

          {/* Column Mapping */}
          <div style={{ background: '#12121f', borderRadius: '16px', padding: '24px', border: '1px solid #1e1e35', marginBottom: '20px' }}>
            <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '700', margin: '0 0 16px' }}>Map Your Columns</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {IMPORT_TYPES.find(t => t.key === importType)?.fields.map(field => {
                const isRequired = IMPORT_TYPES.find(t => t.key === importType)?.required.includes(field);
                return (
                  <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: isRequired ? '#ff6b2b' : '#aaa', minWidth: '120px' }}>
                      {field.replace(/_/g, ' ')}{isRequired ? ' *' : ''}
                    </label>
                    <select value={mapping[field] || ''} onChange={(e) => setMapping({ ...mapping, [field]: e.target.value })} style={s.select}>
                      <option value="">-- skip --</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          <div style={{ background: '#12121f', borderRadius: '16px', border: '1px solid #1e1e35', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e1e35' }}>
              <h3 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', margin: 0 }}>Preview (first 5 rows)</h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>#</th>
                    {Object.entries(mapping).filter(([, v]) => v).map(([field]) => (
                      <th key={field} style={s.th}>{field.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((row, i) => (
                    <tr key={i}>
                      <td style={{ ...s.td, color: '#555' }}>{i + 1}</td>
                      {Object.entries(mapping).filter(([, v]) => v).map(([field, col]) => (
                        <td key={field} style={s.td}>{row[col] || '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => { setStep(2); setFile(null); setHeaders([]); setRows([]); setMapping({}); }}
              style={{ ...s.btn, ...s.btnSecondary }}>← Re-upload</button>
            <button onClick={handleImport} disabled={importing}
              style={{ ...s.btn, ...s.btnPrimary, opacity: importing ? 0.7 : 1, flex: 1 }}>
              {importing ? '⏳ Importing...' : `Import ${rows.length} ${IMPORT_TYPES.find(t => t.key === importType)?.label}`}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && results && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>
            {results.failed === 0 ? '🎉' : results.imported > 0 ? '⚠️' : '❌'}
          </div>
          <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: '800', marginBottom: '8px' }}>
            {results.failed === 0 ? 'Import Complete!' : results.imported > 0 ? 'Import Finished with Issues' : 'Import Failed'}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', maxWidth: '500px', margin: '24px auto' }}>
            <div style={{ background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #1e1e35' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#22c55e' }}>{results.imported}</div>
              <div style={{ fontSize: '12px', color: '#888', fontWeight: '600' }}>Imported</div>
            </div>
            <div style={{ background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #1e1e35' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#f59e0b' }}>{results.skipped}</div>
              <div style={{ fontSize: '12px', color: '#888', fontWeight: '600' }}>Skipped (dupes)</div>
            </div>
            <div style={{ background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #1e1e35' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#ef4444' }}>{results.failed}</div>
              <div style={{ fontSize: '12px', color: '#888', fontWeight: '600' }}>Failed</div>
            </div>
          </div>

          {results.errors && results.errors.length > 0 && (
            <div style={{ background: '#12121f', borderRadius: '14px', padding: '20px', border: '1px solid #2a1a1a', textAlign: 'left', maxWidth: '500px', margin: '0 auto 24px' }}>
              <h4 style={{ color: '#ef4444', fontSize: '13px', fontWeight: '700', margin: '0 0 10px' }}>Errors:</h4>
              {results.errors.slice(0, 10).map((err, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#888', padding: '4px 0', borderBottom: '1px solid #1e1e35' }}>
                  Row {err.row}: {err.error}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => window.location.href = '/app/' + (importType === 'contacts' ? 'customers' : importType === 'products' ? 'inventory' : importType)}
              style={{ ...s.btn, ...s.btnPrimary }}>
              View {IMPORT_TYPES.find(t => t.key === importType)?.label} →
            </button>
            <button onClick={reset} style={{ ...s.btn, ...s.btnSecondary }}>Import Another Type</button>
          </div>

          {/* Quick import next suggestion */}
          <div style={{ marginTop: 28, padding: '20px 24px', background: '#1a1c2e', borderRadius: 14, border: '1px solid #1e1e35', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
            <p style={{ color: '#888', fontSize: 13, fontWeight: 600, margin: '0 0 12px' }}>Import more data:</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              {IMPORT_TYPES.filter(t => t.key !== importType).map(t => (
                <button key={t.key} onClick={() => { setImportType(t.key); setStep(2); setFile(null); setHeaders([]); setRows([]); setMapping({}); setResults(null); }}
                  style={{ background: '#12121f', border: '1px solid #1e1e35', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', transition: 'border-color 0.2s', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff6b2b'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e35'; }}
                >
                  <span style={{ fontSize: 18 }}>{t.icon}</span>
                  <span style={{ color: '#ccc', fontSize: 13, fontWeight: 600 }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Import;
